import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ReactCrop from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import MobileLayout from '../components/MobileLayout';
import ProtectedRoute from '../components/ProtectedRoute';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { User as UserIcon, LogOut, Share2, Heart, Copy, Unlink, Trash2, Camera, Check, Calendar, MessageSquare, Star, AlertTriangle, Edit2 } from 'lucide-react';
import { PointsBadge } from '../components/PointsDisplay';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsContent = ({ user }) => {
  const [relationshipDate, setRelationshipDate] = useState('');
  const [connectionInfo, setConnectionInfo] = useState(null);
  const [showDisconnectDialog, setShowDisconnectDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPictureDialog, setShowPictureDialog] = useState(false);
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [savingName, setSavingName] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [crop, setCrop] = useState({ unit: '%', width: 80, aspect: 1 });
  const [completedCrop, setCompletedCrop] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [userPoints, setUserPoints] = useState(0);
  const [disconnectWarning, setDisconnectWarning] = useState(null);
  const [disconnecting, setDisconnecting] = useState(false);
  const imgRef = useRef(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  
  const fetchConnectionInfo = async () => {
    try {
      const [connRes, statusRes] = await Promise.all([
        axios.get(`${API}/user/connection-info`, { withCredentials: true }),
        axios.get(`${API}/browny/status`, { withCredentials: true }).catch(() => ({ data: { user_points: 0 } }))
      ]);
      setConnectionInfo(connRes.data);
      setUserPoints(statusRes.data?.user_points || 0);
    } catch (error) {
      console.error('Error fetching connection info:', error);
    }
  };
  
  useEffect(() => {
    fetchConnectionInfo();
    if (user?.relationship_start_date) {
      setRelationshipDate(user.relationship_start_date);
    }
    // Populate name fields from user
    if (user?.name) {
      const nameParts = user.name.split(' ');
      setFirstName(nameParts[0] || '');
      setLastName(nameParts.slice(1).join(' ') || '');
    }
  }, [user]);
  
  const validateDate = (dateStr) => {
    const selected = new Date(dateStr);
    const today = new Date();
    today.setHours(23, 59, 59, 999);
    return selected <= today;
  };
  
  const handleRelationshipDateUpdate = async () => {
    if (!relationshipDate) {
      toast.error('Please select a date');
      return;
    }
    
    if (!validateDate(relationshipDate)) {
      toast.error('Date must be today or in the past');
      return;
    }
    
    try {
      await axios.put(
        `${API}/user/relationship-start`,
        { date: relationshipDate },
        { withCredentials: true, params: { date: relationshipDate } }
      );
      toast.success('Relationship date updated!');
      // Refresh user data
      window.location.reload();
    } catch (error) {
      toast.error('Failed to update date');
    }
  };
  
  const handleCopyCode = () => {
    if (connectionInfo?.connection_code) {
      navigator.clipboard.writeText(connectionInfo.connection_code);
      toast.success('Code copied!');
    }
  };
  
  const handleOpenDisconnectDialog = async () => {
    // Fetch warning data about what will be lost
    try {
      const res = await axios.get(`${API}/disconnect/data-loss-warning`, { withCredentials: true });
      setDisconnectWarning(res.data);
      setShowDisconnectDialog(true);
    } catch (error) {
      console.error('Error fetching disconnect warning:', error);
      toast.error('Failed to load disconnection details');
    }
  };
  
  const handleDisconnectPartner = async () => {
    setDisconnecting(true);
    try {
      const res = await axios.post(`${API}/disconnect/partner`, {}, { withCredentials: true });
      toast.success(res.data.message || 'Disconnected from partner');
      setShowDisconnectDialog(false);
      setDisconnectWarning(null);
      // Force full reload to clear all cached data
      window.location.href = '/settings';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to disconnect');
    } finally {
      setDisconnecting(false);
    }
  };
  
  const handleDeleteProfile = async () => {
    try {
      await axios.delete(`${API}/user/profile`, { withCredentials: true });
      toast.success('Profile and all data permanently deleted');
      setShowDeleteDialog(false);
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete profile');
    }
  };
  
  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
      navigate('/login');
    } catch (error) {
      navigate('/login');
    }
  };
  
  const handleSaveName = async () => {
    if (!firstName.trim()) {
      toast.error('First name is required');
      return;
    }
    
    setSavingName(true);
    try {
      await axios.put(
        `${API}/user/name`,
        { first_name: firstName.trim(), last_name: lastName.trim() },
        { withCredentials: true }
      );
      toast.success('Name updated!');
      setShowNameDialog(false);
      // Refresh to show updated name
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update name');
    } finally {
      setSavingName(false);
    }
  };
  
  const openNameDialog = () => {
    // Re-populate from user in case it changed
    if (user?.name) {
      const nameParts = user.name.split(' ');
      setFirstName(nameParts[0] || '');
      setLastName(nameParts.slice(1).join(' ') || '');
    }
    setShowNameDialog(true);
  };
  
  const shareApp = () => {
    const message = "Hey, just a reminder - I want to see your relationship Win! This is something we use in ours. CoupleInapp.com";
    if (navigator.share) {
      navigator.share({ text: message, title: 'CoupleIn' });
    } else {
      navigator.clipboard.writeText(message);
      toast.success('Share message copied!');
    }
  };
  
  const handleImageSelect = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        setSelectedImage(reader.result);
        setShowPictureDialog(true);
      });
      reader.readAsDataURL(e.target.files[0]);
    }
  };
  
  const onImageLoad = useCallback((img) => {
    imgRef.current = img;
    const width = img.width > img.height ? (img.height / img.width) * 80 : 80;
    const height = img.height > img.width ? (img.width / img.height) * 80 : 80;
    const x = (100 - width) / 2;
    const y = (100 - height) / 2;
    
    setCrop({
      unit: '%',
      width,
      height,
      x,
      y,
      aspect: 1
    });
    return false;
  }, []);
  
  const getCroppedImg = async () => {
    if (!completedCrop || !imgRef.current) return null;
    
    const canvas = document.createElement('canvas');
    const image = imgRef.current;
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    
    canvas.width = completedCrop.width * scaleX;
    canvas.height = completedCrop.height * scaleY;
    
    const ctx = canvas.getContext('2d');
    ctx.drawImage(
      image,
      completedCrop.x * scaleX,
      completedCrop.y * scaleY,
      completedCrop.width * scaleX,
      completedCrop.height * scaleY,
      0,
      0,
      canvas.width,
      canvas.height
    );
    
    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.9);
    });
  };
  
  const handleSavePicture = async () => {
    setUploading(true);
    try {
      const croppedBlob = await getCroppedImg();
      if (!croppedBlob) {
        toast.error('Failed to crop image');
        setUploading(false);
        return;
      }
      
      // Convert blob to base64 for storage
      const reader = new FileReader();
      reader.readAsDataURL(croppedBlob);
      reader.onloadend = async () => {
        const base64data = reader.result;
        
        try {
          await axios.put(
            `${API}/user/picture`,
            { picture_data: base64data },
            { withCredentials: true }
          );
          toast.success('Profile picture updated!');
          setShowPictureDialog(false);
          setSelectedImage(null);
          window.location.reload();
        } catch (error) {
          console.error('Save picture error:', error);
          toast.error('Failed to save picture');
          setUploading(false);
        }
      };
    } catch (error) {
      console.error('Process image error:', error);
      toast.error('Failed to process image');
      setUploading(false);
    }
  };
  
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };
  
  return (
    <MobileLayout>
      <div className="p-5 space-y-5" data-testid="settings-page">
        {/* Header with points */}
        <div className="flex justify-between items-center pt-2 pb-4">
          <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
          <PointsBadge points={userPoints} />
        </div>
        
        {/* Browny Points Summary */}
        <div className="bg-yellow-50 rounded-2xl p-4 flex items-center gap-3">
          <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
            <Star className="w-6 h-6 text-yellow-600" fill="currentColor" />
          </div>
          <div className="flex-1">
            <p className="font-bold text-gray-900">{userPoints} Browny Points</p>
            <p className="text-xs text-gray-500">Earn points by logging moods & completing events!</p>
          </div>
        </div>
        
        {/* Profile Section */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5">
          <div className="flex items-center gap-4">
            <div className="relative">
              {user?.picture ? (
                <img 
                  src={user.picture} 
                  alt={user.name}
                  className="w-16 h-16 rounded-full object-cover border-2 border-gray-100"
                />
              ) : (
                <div className="w-16 h-16 gradient-brand rounded-full flex items-center justify-center">
                  <UserIcon className="w-8 h-8 text-white" />
                </div>
              )}
              <button 
                onClick={() => fileInputRef.current?.click()}
                className="absolute -bottom-1 -right-1 w-7 h-7 bg-primary rounded-full flex items-center justify-center shadow-pink border-2 border-white tap-icon"
              >
                <Camera className="w-3.5 h-3.5 text-white" />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageSelect}
                className="hidden"
              />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-semibold text-gray-900 truncate">{user?.name}</p>
                <button 
                  onClick={openNameDialog}
                  className="p-1 hover:bg-gray-100 rounded-full tap-icon"
                  aria-label="Edit name"
                >
                  <Edit2 className="w-3.5 h-3.5 text-gray-400" />
                </button>
              </div>
              <p className="text-sm text-gray-500 truncate">{user?.email}</p>
            </div>
          </div>
        </div>
        
        {/* Connection Code */}
        <div className="bg-gradient-brand-soft rounded-2xl p-5 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">Your Code</span>
            <button
              onClick={handleCopyCode}
              className="flex items-center gap-1.5 text-primary text-sm font-medium tap-feedback"
            >
              <Copy className="w-4 h-4" />
              Copy
            </button>
          </div>
          <div className="text-center py-2">
            <p className="text-3xl font-bold tracking-[0.3em] text-gray-900" data-testid="connection-code">
              {connectionInfo?.connection_code || '------'}
            </p>
          </div>
          <p className="text-xs text-gray-500 text-center">Share this code with your partner</p>
        </div>
        
        {/* Partner Status */}
        {connectionInfo?.has_partner && connectionInfo?.partner && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                <Heart className="w-5 h-5 text-primary" fill="currentColor" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-500">Connected to</p>
                <p className="font-semibold text-gray-900 truncate">{connectionInfo.partner.name}</p>
              </div>
              <Check className="w-5 h-5 text-green-500" />
            </div>
          </div>
        )}
        
        {/* Partner Connection Button */}
        {!connectionInfo?.has_partner && (
          <Button
            onClick={() => navigate('/connection')}
            data-testid="manage-connection-btn"
            className="w-full h-12 rounded-xl gradient-brand shadow-pink font-semibold"
          >
            <Heart className="w-4 h-4 mr-2" />
            Connect Partner
          </Button>
        )}
        
        {/* Relationship Date */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-soft p-5 space-y-4">
          <div>
            <h3 className="font-semibold text-gray-900">Relationship Start Date</h3>
            <p className="text-xs text-gray-500 mt-1">This affects the time together shown on Calendar</p>
          </div>
          <Input
            type="date"
            value={relationshipDate}
            onChange={(e) => setRelationshipDate(e.target.value)}
            max={getTodayDate()}
            data-testid="relationship-date-input"
            className="h-12 rounded-xl border-gray-200"
          />
          <Button
            onClick={handleRelationshipDateUpdate}
            data-testid="save-relationship-date-btn"
            className="w-full h-12 rounded-xl gradient-brand shadow-pink font-semibold"
          >
            Save Date
          </Button>
        </div>
        
        {/* Pricing */}
        <div className="bg-green-50 rounded-2xl p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Pricing</h3>
            <span className="px-3 py-1 bg-green-500 text-white text-xs font-bold rounded-full">FREE</span>
          </div>
          <div>
            <p className="text-xl font-bold text-green-600">Love over profit</p>
            <p className="text-sm text-gray-600 mt-1">We will never charge for these features:</p>
          </div>
          
          {/* Forever Free Features */}
          <div className="space-y-2 py-2">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <Calendar className="w-4 h-4 text-green-600" />
              </div>
              <span className="text-sm font-medium text-gray-700">Calendar & Reminders</span>
              <span className="ml-auto text-xs text-green-600 font-semibold">Forever Free</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <Heart className="w-4 h-4 text-green-600" />
              </div>
              <span className="text-sm font-medium text-gray-700">Mood Sync</span>
              <span className="ml-auto text-xs text-green-600 font-semibold">Forever Free</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                <MessageSquare className="w-4 h-4 text-green-600" />
              </div>
              <span className="text-sm font-medium text-gray-700">Conflict Resolution</span>
              <span className="ml-auto text-xs text-green-600 font-semibold">Forever Free</span>
            </div>
          </div>
          
          <div className="pt-2 border-t border-green-200">
            <p className="text-sm text-gray-600 mb-3">If you want to help our mission, please share this with 3 couples you know:</p>
            <Button
              onClick={shareApp}
              data-testid="share-app-btn"
              variant="outline"
              className="w-full h-11 rounded-xl border-green-300 text-green-700 bg-white"
            >
              <Share2 className="w-4 h-4 mr-2" />
              Share with Couples
            </Button>
          </div>
        </div>
        
        {/* Account Actions */}
        <div className="space-y-3 pt-2">
          {connectionInfo?.has_partner && (
            <Button
              onClick={handleOpenDisconnectDialog}
              data-testid="disconnect-partner-btn"
              variant="outline"
              className="w-full h-12 rounded-xl border-orange-200 text-orange-600"
            >
              <Unlink className="w-4 h-4 mr-2" />
              Disconnect Partner
            </Button>
          )}
          
          <Button
            onClick={handleLogout}
            data-testid="logout-btn"
            variant="outline"
            className="w-full h-12 rounded-xl border-gray-200 text-gray-600"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
          
          <Button
            onClick={() => setShowDeleteDialog(true)}
            data-testid="delete-profile-btn"
            variant="outline"
            className="w-full h-12 rounded-xl border-red-200 text-red-600"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete Profile
          </Button>
        </div>
        
        {/* Picture Crop Dialog */}
        <Dialog open={showPictureDialog} onOpenChange={setShowPictureDialog}>
          <DialogContent className="max-w-sm rounded-2xl">
            <DialogHeader>
              <DialogTitle>Crop Photo</DialogTitle>
            </DialogHeader>
            <div className="flex justify-center py-4">
              {selectedImage && (
                <ReactCrop
                  crop={crop}
                  onChange={(c) => setCrop(c)}
                  onComplete={(c) => setCompletedCrop(c)}
                  aspect={1}
                  circularCrop
                >
                  <img
                    ref={imgRef}
                    src={selectedImage}
                    alt="Crop"
                    onLoad={(e) => onImageLoad(e.currentTarget)}
                    style={{ maxHeight: '300px' }}
                  />
                </ReactCrop>
              )}
            </div>
            <DialogFooter className="flex gap-3">
              <Button
                onClick={() => {
                  setShowPictureDialog(false);
                  setSelectedImage(null);
                }}
                variant="outline"
                className="flex-1 h-11 rounded-xl"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSavePicture}
                disabled={uploading}
                className="flex-1 h-11 rounded-xl gradient-brand"
              >
                {uploading ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Edit Name Dialog */}
        <Dialog open={showNameDialog} onOpenChange={setShowNameDialog}>
          <DialogContent className="max-w-sm rounded-2xl">
            <DialogHeader>
              <DialogTitle>Edit Name</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <p className="text-sm text-gray-500">
                Update your name as you&apos;d like it to appear in the app.
              </p>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">First Name</label>
                <Input
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="First name"
                  className="h-11 rounded-xl"
                  maxLength={50}
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 block mb-1">Last Name (optional)</label>
                <Input
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  placeholder="Last name"
                  className="h-11 rounded-xl"
                  maxLength={50}
                />
              </div>
            </div>
            <DialogFooter className="flex gap-3">
              <Button
                onClick={() => setShowNameDialog(false)}
                variant="outline"
                className="flex-1 h-11 rounded-xl"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveName}
                disabled={savingName || !firstName.trim()}
                className="flex-1 h-11 rounded-xl gradient-brand"
              >
                {savingName ? 'Saving...' : 'Save'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Disconnect Dialog - Enhanced with Data Loss Warning */}
        <Dialog open={showDisconnectDialog} onOpenChange={(open) => {
          setShowDisconnectDialog(open);
          if (!open) setDisconnectWarning(null);
        }}>
          <DialogContent className="max-w-sm rounded-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-center flex items-center justify-center gap-2 text-orange-600">
                <AlertTriangle className="w-5 h-5" />
                Disconnect from Partner?
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <p className="text-center text-gray-600 text-sm">
                This is a <span className="font-semibold text-red-600">permanent action</span>. Disconnecting will delete all shared data and cannot be undone.
              </p>
              
              {disconnectWarning && (
                <div className="bg-red-50 rounded-xl p-4 space-y-3">
                  <p className="text-sm font-semibold text-red-700">You will lose:</p>
                  <ul className="space-y-2">
                    {disconnectWarning.warning_list.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <span className="text-red-500 mt-0.5">•</span>
                        <div>
                          <span className="font-medium text-gray-900">{item.item}</span>
                          {item.count > 0 && (
                            <span className="text-red-600 font-semibold ml-1">({item.count})</span>
                          )}
                          <p className="text-gray-500 text-xs">{item.description}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              <div className="bg-yellow-50 rounded-lg p-3">
                <p className="text-xs text-yellow-800">
                  <span className="font-semibold">Note:</span> Your partner {connectionInfo?.partner?.name || ''} will be notified that you disconnected. They will also lose access to shared data.
                </p>
              </div>
            </div>
            
            <DialogFooter className="flex flex-col gap-3 mt-4">
              <Button
                onClick={handleDisconnectPartner}
                disabled={disconnecting}
                className="w-full h-12 rounded-xl bg-red-500 hover:bg-red-600 text-white"
              >
                {disconnecting ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Disconnecting...
                  </span>
                ) : (
                  'Yes, Disconnect & Delete Data'
                )}
              </Button>
              <Button
                onClick={() => {
                  setShowDisconnectDialog(false);
                  setDisconnectWarning(null);
                }}
                variant="outline"
                className="w-full h-12 rounded-xl"
              >
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        
        {/* Delete Dialog - Enhanced with Complete Deletion Warning */}
        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent className="max-w-sm rounded-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-center text-red-600 flex items-center justify-center gap-2">
                <Trash2 className="w-5 h-5" />
                Delete Profile Permanently?
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <p className="text-center text-gray-600 text-sm">
                This action is <span className="font-bold text-red-600">IRREVERSIBLE</span>. 
                We will delete <span className="font-semibold">ALL</span> your data from our database.
              </p>
              
              <div className="bg-red-50 rounded-xl p-4 space-y-2">
                <p className="text-sm font-semibold text-red-700">Everything will be deleted:</p>
                <ul className="text-sm text-gray-700 space-y-1">
                  <li className="flex items-center gap-2">
                    <span className="text-red-500">✗</span> Your profile and account
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-red-500">✗</span> All calendar events and reminders
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-red-500">✗</span> Your entire mood journal
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-red-500">✗</span> All browny points and trades
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-red-500">✗</span> Conflict resolution history
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-red-500">✗</span> Partner connection
                  </li>
                </ul>
              </div>
              
              {connectionInfo?.has_partner && (
                <div className="bg-yellow-50 rounded-lg p-3">
                  <p className="text-xs text-yellow-800">
                    <span className="font-semibold">Note:</span> Your partner {connectionInfo.partner?.name || ''} will be notified 
                    and their browny points will be reset.
                  </p>
                </div>
              )}
              
              <div className="bg-blue-50 rounded-lg p-3">
                <p className="text-xs text-blue-800">
                  <span className="font-semibold">💡 Tip:</span> Export your data first if you want to keep a copy.
                </p>
              </div>
            </div>
            
            <DialogFooter className="flex flex-col gap-3 mt-4">
              <Button
                onClick={handleDeleteProfile}
                className="w-full h-12 rounded-xl bg-red-500 hover:bg-red-600 text-white"
              >
                Yes, Delete Everything Permanently
              </Button>
              <Button
                onClick={() => setShowDeleteDialog(false)}
                variant="outline"
                className="w-full h-12 rounded-xl"
              >
                Cancel
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MobileLayout>
  );
};

const Settings = () => {
  return (
    <ProtectedRoute>
      <SettingsContent />
    </ProtectedRoute>
  );
};

export default Settings;
